import React, { useState } from 'react';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Ship,
  Calendar,
  User,
  Building2,
  FileText,
  Clock
} from 'lucide-react';

const Drydocs = () => {
  const [searchTerm, setSearchTerm] = useState('');

  // Données de test pour les drydocs
  const drydocs = [
    {
      id: 1,
      customer_id: 1,
      customer_name: 'CMA CGM',
      customer_recipient_id: 1,
      customer_recipient_name: 'CMA CGM Marseille',
      imo_number: 'IMO1234567',
      date_arrivee: '2024-02-01',
      date_depart: '2024-02-15',
      notes: 'Maintenance moteur principal et révision générale',
      author_name: 'Jean Dupont',
      date_creation: '2024-01-25',
      status: 'En cours',
      nombreSaisies: 3
    },
    {
      id: 2,
      customer_id: 2,
      customer_name: 'MSC',
      customer_recipient_id: 2,
      customer_recipient_name: 'MSC Terminal',
      imo_number: 'IMO7654321',
      date_arrivee: '2024-02-10',
      date_depart: '2024-02-25',
      notes: 'Réparation coque et peinture',
      author_name: 'Marie Martin',
      date_creation: '2024-02-05',
      status: 'En cours',
      nombreSaisies: 5
    },
    {
      id: 3,
      customer_id: 3,
      customer_name: 'Evergreen',
      customer_recipient_id: 3,
      customer_recipient_name: 'Evergreen Marseille',
      imo_number: 'IMO1112223',
      date_arrivee: '2024-01-15',
      date_depart: '2024-01-30',
      notes: 'Entretien annuel complet',
      author_name: 'Pierre Durand',
      date_creation: '2024-01-10',
      status: 'Terminé',
      nombreSaisies: 8
    },
    {
      id: 4,
      customer_id: 1,
      customer_name: 'CMA CGM',
      customer_recipient_id: 1,
      customer_recipient_name: 'CMA CGM Marseille',
      imo_number: 'IMO9876543',
      date_arrivee: '2024-02-20',
      date_depart: '2024-03-05',
      notes: 'Rénovation des cabines et espaces communs',
      author_name: 'Sophie Blanc',
      date_creation: '2024-02-15',
      status: 'Planifié',
      nombreSaisies: 0
    }
  ];

  const filteredDrydocs = drydocs.filter(drydoc =>
    drydoc.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    drydoc.customer_recipient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    drydoc.imo_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    drydoc.notes.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch(status) {
      case 'En cours': return 'bg-atl-blue-100 text-atl-blue-800';
      case 'Terminé': return 'bg-atl-green-100 text-atl-green-800';
      case 'Planifié': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-port-gray-100 text-port-gray-800';
    }
  };

  const calculateDuration = (dateArrivee, dateDepart) => {
    if (!dateArrivee || !dateDepart) return 'Non définie';
    const start = new Date(dateArrivee);
    const end = new Date(dateDepart);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} jour${diffDays > 1 ? 's' : ''}`;
  };

  const handleViewSaisies = (drydocId) => {
    // Navigation vers la liste des saisies filtrées par drydoc
    window.location.href = `/saisies?drydoc=${drydocId}`;
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-port-gray-900">Gestion des Drydocs</h1>
          <p className="text-port-gray-600">Suivi des bateaux en cale sèche</p>
        </div>
        <button 
          onClick={() => window.location.href = '/nouveau-drydoc'}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Nouveau Drydoc</span>
        </button>
      </div>

      {/* Recherche */}
      <div className="card p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-port-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par client, bateau, IMO, notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-atl-blue-100 rounded-lg">
              <Ship className="h-6 w-6 text-atl-blue-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-port-gray-900">
                {drydocs.length}
              </div>
              <div className="text-sm text-port-gray-600">Total Drydocs</div>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-atl-blue-100 rounded-lg">
              <Clock className="h-6 w-6 text-atl-blue-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-port-gray-900">
                {drydocs.filter(d => d.status === 'En cours').length}
              </div>
              <div className="text-sm text-port-gray-600">En cours</div>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-atl-green-100 rounded-lg">
              <FileText className="h-6 w-6 text-atl-green-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-port-gray-900">
                {drydocs.reduce((sum, d) => sum + d.nombreSaisies, 0)}
              </div>
              <div className="text-sm text-port-gray-600">Saisies totales</div>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Calendar className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-port-gray-900">
                {drydocs.filter(d => d.status === 'Planifié').length}
              </div>
              <div className="text-sm text-port-gray-600">Planifiés</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau des drydocs */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-port-gray-200">
            <thead className="bg-port-gray-50">
              <tr>
                <th className="table-header px-6 py-3 text-left">Bateau</th>
                <th className="table-header px-6 py-3 text-left">Client</th>
                <th className="table-header px-6 py-3 text-left">Période</th>
                <th className="table-header px-6 py-3 text-left">Durée</th>
                <th className="table-header px-6 py-3 text-left">Notes</th>
                <th className="table-header px-6 py-3 text-left">Statut</th>
                <th className="table-header px-6 py-3 text-left">Saisies</th>
                <th className="table-header px-6 py-3 text-left">Créé par</th>
                <th className="table-header px-6 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-port-gray-200">
              {filteredDrydocs.map((drydoc) => (
                <tr key={drydoc.id} className="table-row">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Ship className="h-8 w-8 text-atl-blue-600 mr-3" />
                      <div>
                        <div className="font-medium text-port-gray-900">{drydoc.customer_recipient_name}</div>
                        <div className="text-sm text-port-gray-500">IMO: {drydoc.imo_number}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Building2 className="h-6 w-6 text-atl-green-600 mr-2" />
                      <span className="font-medium text-port-gray-900">{drydoc.customer_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center text-port-gray-900">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span className="font-medium">Arrivée:</span>
                        <span className="ml-1">{new Date(drydoc.date_arrivee).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="flex items-center text-port-gray-900">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span className="font-medium">Départ:</span>
                        <span className="ml-1">{new Date(drydoc.date_depart).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-port-gray-900">
                      {calculateDuration(drydoc.date_arrivee, drydoc.date_depart)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-port-gray-900 max-w-xs truncate" title={drydoc.notes}>
                      {drydoc.notes}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(drydoc.status)}`}>
                      {drydoc.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => handleViewSaisies(drydoc.id)}
                      className="text-atl-blue-600 hover:text-atl-blue-700 font-medium"
                    >
                      {drydoc.nombreSaisies}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-port-gray-400 mr-1" />
                      <div className="text-sm">
                        <div className="text-port-gray-900">{drydoc.author_name}</div>
                        <div className="text-port-gray-500">{new Date(drydoc.date_creation).toLocaleDateString('fr-FR')}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => handleViewSaisies(drydoc.id)}
                        className="text-atl-blue-600 hover:text-atl-blue-700"
                        title="Voir les saisies de ce drydoc"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => window.location.href = `/modifier-drydoc/${drydoc.id}`}
                        className="text-port-gray-600 hover:text-port-gray-700"
                        title="Modifier les informations du drydoc"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-700"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Drydocs;
